type AudioWindow = Window &
  typeof globalThis & {
    webkitAudioContext?: typeof AudioContext;
  };

interface ToneSequenceConfig {
  frequencies: number[];
  noteDuration: number;
  noteSpacing: number;
  peakGain: number;
  repeats: number;
  repeatGap: number;
}

const DASHBOARD_SUCCESS_SOUND = {
  frequencies: [523.25, 659.25, 783.99],
  noteDuration: 0.15,
  noteSpacing: 0.1,
  peakGain: 0.3,
  repeats: 1,
  repeatGap: 0,
};

const LIVECHAT_NOTIFICATION_SOUND = {
  frequencies: [523.25, 659.25, 783.99],
  noteDuration: 0.26,
  noteSpacing: 0.13,
  peakGain: 0.58,
  repeats: 1,
  repeatGap: 0,
};

// Two descending notes so the periodic "a chat is still waiting" reminder is
// recognizably different from the three-note new-activity chime.
const LIVECHAT_REMINDER_SOUND = {
  frequencies: [659.25, 523.25],
  noteDuration: 0.26,
  noteSpacing: 0.16,
  peakGain: 0.58,
  repeats: 1,
  repeatGap: 0,
};

const CRITICAL_BUG_SIREN = {
  frequencies: [880, 520, 880, 520, 880, 520, 880, 520],
  noteDuration: 0.42,
  noteSpacing: 0.34,
  peakGain: 0.72,
  repeats: 2,
  repeatGap: 0.12,
};

function getChimeDuration({ frequencies, noteDuration, noteSpacing }: ToneSequenceConfig): number {
  return (frequencies.length - 1) * noteSpacing + noteDuration;
}

async function playToneSequence(
  browserWindow: AudioWindow,
  config: ToneSequenceConfig,
): Promise<boolean> {
  const AudioContextCtor = browserWindow?.AudioContext || browserWindow?.webkitAudioContext;
  if (typeof AudioContextCtor !== "function") {
    return false;
  }

  const { frequencies, noteDuration, noteSpacing, peakGain, repeats, repeatGap } = config;
  const chimeDuration = getChimeDuration(config);
  const totalDuration = chimeDuration * repeats + (repeats > 1 ? repeatGap * (repeats - 1) : 0);

  try {
    const audioContext = new AudioContextCtor();
    if (audioContext.state === "suspended" && typeof audioContext.resume === "function") {
      // Autoplay policy can leave resume() pending until the next user
      // gesture, which would play the chime long after the event it
      // announces. Give it a brief window, then bail instead of chiming late.
      await Promise.race([
        Promise.resolve(audioContext.resume()).catch(() => {}),
        new Promise((resolve) => window.setTimeout(resolve, 250)),
      ]);
      if ((audioContext.state as AudioContextState) !== "running") {
        if (typeof audioContext.close === "function") {
          void audioContext.close();
        }
        return false;
      }
    }

    for (let repeatIndex = 0; repeatIndex < repeats; repeatIndex += 1) {
      const repeatOffset = repeatIndex * (chimeDuration + repeatGap);

      frequencies.forEach((frequency, index) => {
        const startAt = audioContext.currentTime + repeatOffset + index * noteSpacing;
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);

        oscillator.frequency.setValueAtTime(frequency, startAt);
        oscillator.type = "sine";

        gainNode.gain.setValueAtTime(0, startAt);
        gainNode.gain.linearRampToValueAtTime(peakGain, startAt + 0.01);
        gainNode.gain.exponentialRampToValueAtTime(0.01, startAt + noteDuration);

        oscillator.start(startAt);
        oscillator.stop(startAt + noteDuration);
      });
    }

    if (typeof audioContext.close === "function") {
      window.setTimeout(
        () => {
          void audioContext.close();
        },
        Math.ceil(totalDuration * 1000) + 100,
      );
    }

    return true;
  } catch {
    return false;
  }
}

export async function playDashboardSuccessSound(
  browserWindow: AudioWindow = window,
): Promise<boolean> {
  return playToneSequence(browserWindow, DASHBOARD_SUCCESS_SOUND);
}

export async function playLivechatNotificationSound(
  browserWindow: AudioWindow = window,
): Promise<boolean> {
  return playToneSequence(browserWindow, LIVECHAT_NOTIFICATION_SOUND);
}

export async function playLivechatReminderSound(
  browserWindow: AudioWindow = window,
): Promise<boolean> {
  return playToneSequence(browserWindow, LIVECHAT_REMINDER_SOUND);
}

/** A deliberately urgent, alternating tone reserved for newly submitted
 * critical bugs. Keeping it synthesized avoids loading or buffering an audio
 * file after the event has happened. */
export async function playCriticalBugSiren(browserWindow: AudioWindow = window): Promise<boolean> {
  return playToneSequence(browserWindow, CRITICAL_BUG_SIREN);
}
